import '@/globals/css/base.css' // must be first — establishes @layer order before lab-components IIFE runs
import 'vue-sonner/style.css'
import '@/globals/css/vue-sonner-overrides.css' // must come after vue-sonner/style.css to win the cascade
import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { d, i18n, loadRemoteI18n, t } from '@/locales'
import { ENV } from './globals/typescript/utils/host'
import {
  applyEnvBarHeight,
  applyEnvFavicon,
} from './globals/typescript/utils/envIndicator'
import { initFirebase } from '@/firebase/init'
import { initSentry } from './globals/typescript/utils/sentry'
import { VersionCheckPlugin } from '@team-milva/lab-vue-plugins'
import { handleCriticalError } from '@/globals/typescript/utils/handleCriticalError'
import { vTooltip } from '@/globals/typescript/directives/tooltip'

import {
  LAB_ADMIN_LOCALES,
  type TLabAdminLocale,
} from '@team-milva/lab-models/language'

async function bootstrap() {
  // Resolve the runtime environment and initialize Firebase before anything else
  await initFirebase()

  // Non-prod gets a colored tab icon and gives up 18px at the top of the
  // window. Both depend on the project ID initFirebase() just resolved, so
  // neither can run at module scope.
  applyEnvFavicon()
  applyEnvBarHeight()

  // Ensure translations are up-to-date before app mounts
  await loadRemoteI18n()
  const app = createApp(App)
  const pinia = createPinia() // create the instance

  const savedLang = localStorage.getItem('lang') as TLabAdminLocale | null
  let resolvedLang: TLabAdminLocale = 'en'

  if (savedLang && LAB_ADMIN_LOCALES.includes(savedLang)) {
    resolvedLang = savedLang
  } else {
    const browserLanguages = navigator.languages?.length
      ? navigator.languages
      : [navigator.language]
    for (const lang of browserLanguages) {
      const langCode = (
        lang.split('-')[0] ?? ''
      ).toLowerCase() as TLabAdminLocale
      if (LAB_ADMIN_LOCALES.includes(langCode)) {
        resolvedLang = langCode
        break
      }
    }
  }

  i18n.global.locale.value = resolvedLang

  app.use(i18n)

  // vue-i18n's own $d hands the bare content locale ('en') to Intl, which ICU
  // resolves to en-US — month-first dates, inconsistent with Danish. Our d()
  // maps the content locale onto a display locale instead (resolveIntlLocale),
  // so swap it in for every $d() call site in templates. The $d signature in
  // src/types/vue-augmentations.d.ts already describes this helper.
  app.config.globalProperties.$d = d
  app.use(pinia)
  app.use(router)
  app.use(VersionCheckPlugin, {
    currentVersion: import.meta.env.VITE_APP_VERSION,
    overlayText: () => t('message.updating-to-latest-version'),
    router,
    disabled: false, // import.meta.env.MODE === 'development'
    onError: ({ error, latestVersion, cachedVersion, reloadTargetVersion }) =>
      handleCriticalError({
        error,
        code: 'version-update-error',
        debugMessages: [
          `latestVersion: ${latestVersion}`,
          `cachedVersion: ${cachedVersion}`,
          `reloadTargetVersion: ${reloadTargetVersion}`,
        ],
        title: 'Version update fail',
      }),
  })
  app.directive('tooltip', vTooltip)
  app.provide('translateFormError', getErrorMessage)

  // init auth before mounting + before first route decisions
  const authStore = useAuthStore(pinia) // pass pinia when outside setup()
  await authStore.init()

  if (!ENV.isLocalhost) {
    initSentry({ app, router })
  }

  app.mount('#app')
}

bootstrap()

